import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { Subscription } from 'rxjs'

enum SectionType {
  UserManagement = 'user_management',
  SystemManagement = 'system_management',
  SystemConfiguration = 'system_configuration',
  MobileSystemConfiguration = 'mobile_system_configuration',
  SystemSettings = 'system_settings',
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
})
export class AdminComponent implements OnInit, OnDestroy {
  public sectionsWithPages = []
  public adminPages = []
  public results = []
  public adminSections = [
    {
      key: SectionType.UserManagement,
      title: 'USER_MANAGEMENT',
      permissions: ['USER_MANAGEMENT_PANEL'],
    },
    {
      key: SectionType.SystemManagement,
      title: 'SYSTEM_MANAGEMENT',
      permissions: ['SYSTEM_MANAGEMENT_PANEL'],
    },
    {
      key: SectionType.SystemConfiguration,
      title: 'SYSTEM_CONFIGURATION',
      permissions: ['SYSTEM_CONFIGURATION_PANEL'],
    },
    {
      key: SectionType.MobileSystemConfiguration,
      title: 'MOBILE_SYSTEM_CONFIGURATION',
      permissions: ['MOBILE_SYSTEM_CONFIGURATION_PANEL'],
    },
    {
      key: SectionType.SystemSettings,
      title: 'SYSTEM_SETTINGS',
      permissions: ['SYSTEM_PANEL'],
    },
  ]
  public searchForm: FormGroup
  public valueChanges$: Subscription
  public langChange$: Subscription

  constructor(
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
  ) {
    this.adminPages = this.generatePages()
    this.sectionsWithPages = this.adminSections.map((section) => ({
      ...section,
      pages: this.adminPages.filter((page) => page.sectionType === section.key),
    }))

    this.searchForm = this.formBuilder.group({
      keyword: new FormControl(),
    })

    this.valueChanges$ = this.searchForm.valueChanges.subscribe(() => {
      const keyword = this.searchForm.get('keyword').value
      this.results = this.adminPages.filter(({ title }) =>
        title.toLowerCase().includes(keyword.toLowerCase()),
      )
    })

    this.langChange$ = translateService.onLangChange.subscribe(() => {
      this.adminPages = this.generatePages()

      if (!!this.keyword) {
        this.results = this.adminPages.filter(({ title }) =>
          title.toLowerCase().includes(this.keyword.toLowerCase()),
        )
      }
    })
  }

  public get keyword() {
    return this.searchForm.get('keyword').value
  }

  public generatePages() {
    return [
      {
        title_key: 'USERS',
        title: this.translateService.instant('USERS'),
        link: ['/admin/users'],
        icon: '/assets/multiple-users-silhouette.svg',
        sectionType: SectionType.UserManagement,
        permissions: ['USER_MANAGEMENT'],
      },
      {
        title_key: 'USER_ROLES',
        title: this.translateService.instant('USER_ROLES'),
        link: ['/admin/roles'],
        icon: '/assets/shield.svg',
        sectionType: SectionType.UserManagement,
        permissions: ['USER_ROLES'],
      },
      {
        title_key: 'COMMUNITIES',
        title: this.translateService.instant('COMMUNITIES'),
        link: ['/admin/communities'],
        icon: '/assets/global.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['COMMUNITIES'],
      },
      {
        title_key: 'CONTACT_TITLES',
        title: this.translateService.instant('CONTACT_TITLES'),
        link: ['/admin/contact-titles'],
        icon: '/assets/identification.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['CONTACT_TITLES'],
      },
      {
        title_key: 'CONTACT_TYPES',
        title: this.translateService.instant('CONTACT_TYPES'),
        link: ['/admin/contact-types'],
        icon: '/assets/contact.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['CONTACT_TYPES'],
      },
      {
        title_key: 'DISCOUNT_GROUPS',
        title: this.translateService.instant('DISCOUNT_GROUPS'),
        link: ['/admin/discount-groups'],
        icon: '/assets/discount.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['DISCOUNT_GROUPS'],
      },
      {
        title_key: 'FINANCICAL_INSTITUTIONS',
        title: this.translateService.instant('FINANCICAL_INSTITUTIONS'),
        link: ['/admin/financial-institutions'],
        icon: '/assets/bank.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['FINANCIAL_INSTITUTIONS'],
      },
      {
        title_key: 'PAYMENT_PURPOSES',
        title: this.translateService.instant('PAYMENT_PURPOSES'),
        link: ['/admin/payment-purposes'],
        icon: '/assets/pay.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['PAYMENT_PURPOSES'],
      },
      {
        title_key: 'REGISTRATIONS',
        title: this.translateService.instant('REGISTRATIONS'),
        link: ['/admin/registrations'],
        icon: '/assets/enterprise.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['ORGANIZATIONS'],
      },
      {
        title_key: 'STREETS',
        title: this.translateService.instant('STREETS'),
        link: ['/admin/streets'],
        icon: '/assets/street.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['STREETS'],
      },
      {
        title_key: 'STREET_TYPES',
        title: this.translateService.instant('STREET_TYPES'),
        link: ['/admin/street-types'],
        icon: '/assets/street-sign.svg',
        sectionType: SectionType.SystemManagement,
        permissions: ['STREET_TYPES'],
      },
      // {
      //   title_key: 'WARDS',
      //   title: this.translateService.instant('WARDS'),
      //   link: ['/admin/wards'],
      //   icon: '/assets/ward.svg',
      //   sectionType: SectionType.SystemManagement,
      // },
      {
        title_key: 'FORMS',
        title: this.translateService.instant('FORMS'),
        link: ['/admin/forms'],
        icon: '/assets/forms.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['FORMS'],
      },
      {
        title_key: 'FORM_POSTPROCESSORS',
        title: this.translateService.instant('FORM_POSTPROCESSORS'),
        link: ['/admin/form-postprocessors'],
        icon: '/assets/forms.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['POSTPROCESSOR_SETTINGS'],
      },
      // {
      //   title_key: 'INDUSTRIES',
      //   title: this.translateService.instant('INDUSTRIES'),
      //   link: ['/admin/industries'],
      //   icon: '/assets/factory.svg',
      //   sectionType: SectionType.SystemConfiguration,
      // },
      {
        title_key: 'IMAGE_TYPES',
        title: this.translateService.instant('IMAGE_TYPES'),
        link: ['/admin/image-types'],
        icon: '/assets/photo-of-a-landscape.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['IMAGE_TYPES'],
      },
      {
        title_key: 'TEMPLATES',
        title: this.translateService.instant('TEMPLATES'),
        link: ['/admin/templates'],
        icon: '/assets/report.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['TEMPLATES'],
      },
      {
        title_key: 'DATA_CYCLES',
        title: this.translateService.instant('DATA_CYCLES'),
        link: ['/admin/data-cycles'],
        icon: '/assets/data-cycle.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['DATA_CYCLE_MANAGEMENT'],
      },
      {
        title_key: 'PROPERTY_TYPE_TEMPLATES',
        title: this.translateService.instant('PROPERTY_TYPE_TEMPLATES'),
        link: ['/admin/property-type-templates'],
        icon: '/assets/theme.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['PROPERTY_TYPE_TEMPLATES'],
      },
      {
        title_key: 'PDF_BATCH_GENERATION',
        title: this.translateService.instant('PDF_BATCH_GENERATION'),
        link: ['/admin/rdn-generation'],
        icon: '/assets/contract.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['PDF_BATCH_GENERATION'],
      },
      {
        title_key: 'DOCUMENT_TYPES',
        title: this.translateService.instant('DOCUMENT_TYPES'),
        link: ['/admin/document-types'],
        icon: '/assets/paper.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['DOCUMENT_TYPES'],
      },
      // {
      //   title_key: 'COMMERCIAL_LICENSE_MAPPINGS',
      //   title: this.translateService.instant('COMMERCIAL_LICENSE_MAPPINGS'),
      //   link: ['/admin/commercial-license-mappings'],
      //   icon: '/assets/stamp.svg',
      //   sectionType: SectionType.SystemConfiguration,
      // },
      {
        title_key: 'FIXED_TEMPLATES',
        title: this.translateService.instant('FIXED_TEMPLATES'),
        link: ['/admin/fixed-templates'],
        icon: '/assets/template.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['FIXED_TEMPLATES'],
      },
      {
        title_key: 'INTERNATIONALIZATION',
        title: this.translateService.instant('INTERNATIONALIZATION'),
        link: ['/admin/i18n/en/list'],
        icon: '/assets/i18n.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['INTERNATIONALIZATION'],
      },
      {
        title_key: 'LANGUAGES',
        title: this.translateService.instant('LANGUAGES'),
        link: ['/admin/languages/list'],
        icon: '/assets/languages.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['LANGUAGES'],
      },
      {
        title_key: 'GIS_LAYERS',
        title: this.translateService.instant('GIS_LAYERS'),
        link: ['/admin/layer'],
        icon: '/assets/layer.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['GIS_LAYERS'],
      },
      {
        title_key: 'LICENSE_TYPES',
        title: this.translateService.instant('LICENSE_TYPES'),
        link: ['/admin/license-types'],
        icon: '/assets/legal-contract-paper-icon.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['LICENSE_TYPES'],
      },
      {
        title_key: 'LICENSE_CATEGORIES',
        title: this.translateService.instant('BUSINESS_CATEGORIES'),
        link: ['/admin/license-categories'],
        icon: '/assets/business-categories.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['LICENSE_CATEGORIES'],
      },
      {
        title_key: 'MOBILE_SETTINGS',
        title: this.translateService.instant('MOBILE_SETTINGS'),
        link: ['/admin/mobile-settings'],
        icon: '/assets/system.svg',
        sectionType: SectionType.MobileSystemConfiguration,
        permissions: ['MOBILE_SETTINGS'],
      },
      {
        title_key: 'MOBILE_USERS',
        title: this.translateService.instant('MOBILE_USERS'),
        link: ['/admin/mobile-users'],
        icon: '/assets/multiple-users-silhouette.svg',
        sectionType: SectionType.MobileSystemConfiguration,
        permissions: ['MOBILE_USERS'],
      },
      {
        title_key: 'TASKS',
        title: this.translateService.instant('TASKS'),
        link: ['/admin/tasks'],
        icon: '/assets/tasks.svg',
        sectionType: SectionType.MobileSystemConfiguration,
        permissions: ['MOBILE_TASKS'],
      },
      {
        title_key: 'NOT_DELIVERED_REASON',
        title: this.translateService.instant('NOT_DELIVERED_REASON'),
        link: ['/admin/not-delivered-reason'],
        icon: '/assets/tasks.svg',
        sectionType: SectionType.MobileSystemConfiguration,
        permissions: ['MOBILE_NOT_DELIVERED_REASON'],
      },
      {
        title_key: 'SYSTEM_SETTINGS',
        title: this.translateService.instant('SYSTEM_SETTINGS'),
        link: ['/admin/settings/system_settings'],
        icon: '/assets/system.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['SYSTEM_SETTINGS'],
      },
      {
        title_key: 'SECURITY_SETTINGS',
        title: this.translateService.instant('SECURITY_SETTINGS'),
        link: ['/admin/settings/security_settings'],
        icon: '/assets/security.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['SECURITY_SETTINGS'],
      },
      {
        title_key: 'SMTP_SETTINGS',
        title: this.translateService.instant('SMTP_SETTINGS'),
        link: ['/admin/settings/smtp_settings'],
        icon: '/assets/smtp.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['SMTP_SETTINGS'],
      },
      {
        title_key: 'GIS_SETTINGS',
        title: this.translateService.instant('GIS_SETTINGS'),
        link: ['/admin/settings/gis_settings'],
        icon: '/assets/gis1.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['GIS_SETTINGS'],
      },
      {
        title_key: 'PATH_SETTINGS',
        title: this.translateService.instant('PATH_SETTINGS'),
        link: ['/admin/settings/path_settings'],
        icon: '/assets/path.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['PATH_SETTINGS'],
      },
      {
        title_key: 'SETTINGS_UNITS',
        title: this.translateService.instant('SETTINGS_UNITS'),
        link: ['/admin/settings/units_settings'],
        icon: '/assets/units.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['UNIT_SETTINGS'],
      },
      {
        title_key: 'SETTINGS_LICENSE_GENERATION',
        title: this.translateService.instant('LICENSE_GENERATION'),
        link: ['/admin/settings/license_generation_settings'],
        icon: '/assets/legal-contract-paper-icon.svg',
        sectionType: SectionType.SystemSettings,
      },
      {
        title_key: 'SETTINGS_UI',
        title: this.translateService.instant('SETTINGS_UI'),
        link: ['/admin/settings/ui_settings'],
        icon: '/assets/website-layout-icon.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['UI_SETTINGS'],
      },
      {
        title_key: 'SETTINGS_EXTERNAL_DASHBOARDS',
        title: this.translateService.instant('SETTINGS_EXTERNAL_DASHBOARDS'),
        link: ['/admin/settings/external_dashboards_settings'],
        icon: '/assets/website-layout-icon.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['EXTERNAL_DASHBOARD_SETTINGS'],
      },
      {
        title_key: 'COMPOUND_SETTINGS',
        title: this.translateService.instant('COMPOUND_SETTINGS'),
        link: ['/admin/compound-settings'],
        icon: '/assets/compound.svg',
        sectionType: SectionType.SystemSettings,
        permissions: ['SYSTEM_SETTINGS'],
      },
      {
        title_key: 'STATIC_CONTENT_TEMPLATES',
        title: this.translateService.instant('STATIC_CONTENT_TEMPLATES'),
        link: ['/admin/static-content-templates'],
        icon: '/assets/document-edit-icon.svg',
        sectionType: SectionType.SystemConfiguration,
        permissions: ['STATIC_CONTENT_TEMPLATES'],
      },
    ]
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.valueChanges$.unsubscribe()
    this.langChange$.unsubscribe()
  }
}
